import { useState, useEffect } from "react";
import "@/App.css";
import {Pizza, Phone, Navigation, Search, Check, AlertCircle, MessageCircleCode} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

// Backend API URL from environment variables
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const GENERIC_LAT = 13.69841;
const GENERIC_LNG = -89.192352;


export default function App() {
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [orderDetails, setOrderDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingBranches, setIsLoadingBranches] = useState(true);
  const [error, setError] = useState(null);

  // Fetch branches on component mount
  useEffect(() => {
    fetchBranches();
  }, []);

  // Fetch branches from API
  const fetchBranches = async () => {
    try {
      setIsLoadingBranches(true);
      const response = await axios.get(`${BACKEND_URL}/api/v1/core/branches/`);
      setBranches(response.data.results);
      setError(null);
    } catch (err) {
      console.error("Error fetching branches:", err);
      setError("No se pudieron cargar las sucursales");
      toast.error("Error al cargar sucursales");
    } finally {
      setIsLoadingBranches(false);
    }
  };

  // Fetch order details from API
  const handleSearchOrder = async () => {
    if (!selectedBranch) {
      toast.error("Por favor selecciona una sucursal primero");
      return;
    }

    if (!orderNumber.trim()) {
      toast.error("Por favor ingresa un número de orden");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/core/branches/${selectedBranch}/orders/${orderNumber.trim()}/`,
      );
      if (response.data) {
        setOrderDetails(response.data);
        toast.success("¡Orden encontrada!");
      }
    } catch (err) {
      console.error("Error fetching order:", err);
      setOrderDetails(null);

      if (err.response?.status === 404) {
        toast.error("Orden no encontrada");
      } else {
        toast.error("Error al buscar la orden");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Open Waze with coordinates
  const handleOpenWaze = () => {
    // Use coordinates from order data if available, otherwise use fixed coordinates
    const lat = orderDetails?.coordinates?.lat || 0;
    const lng = orderDetails?.coordinates?.lng || 0;
    
    const wazeUrl = `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
    window.open(wazeUrl, "_blank");
    toast.success("Abriendo Waze...");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchOrder();
    }
  };

  const isGeneric =
      orderDetails?.coordinates?.lat === GENERIC_LAT &&
      orderDetails?.coordinates?.lng === GENERIC_LNG;
  const hasCoordinates = !!(orderDetails?.coordinates?.lat && orderDetails?.coordinates?.lng);
  const isNavigable = hasCoordinates && !isGeneric;

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden" style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* Hero Header with Domino's Colors */}
      <div
        className="relative h-32 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1650327381366-c6dc88f8b9fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxnb3VybWV0JTIwcGl6emElMjBjbG9zZSUyMHVwJTIwdGV4dHVyZXxlbnwwfHx8fDE3NjkwOTYyMjh8MA&ixlib=rb-4.1.0&q=85')`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-blue-700/90 to-blue-600/95"></div>
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center">
            <Pizza className="w-12 h-12 text-white mx-auto mb-2" />
            <h1
              className="text-3xl font-black text-white tracking-tight"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Domino's Pizza
            </h1>
            <p className="text-blue-100 text-sm font-semibold mt-1">Sistema de Entregas</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-6 py-6 pb-32">
        {/* Branch Selection */}
        <div className="mb-6">
          <Label className="text-gray-700 font-bold text-sm uppercase tracking-wide mb-2 block">
            Seleccionar Sucursal
          </Label>
          {isLoadingBranches ? (
            <div className="h-14 bg-white border border-gray-300 rounded-xl shadow-sm flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <Select value={selectedBranch} onValueChange={setSelectedBranch}>
              <SelectTrigger
                data-testid="branch-select"
                className="h-14 text-lg bg-white border-gray-300 focus:border-blue-600 focus:ring-blue-600 rounded-xl shadow-sm w-full"
              >
                <SelectValue placeholder="Elige tu sucursal..." />
              </SelectTrigger>
              <SelectContent className="max-w-[calc(100vw-3rem)]">
                {branches.map((branch) => (
                  <SelectItem key={branch.id} value={branch.id} className="text-base">
                    {branch.code} - {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Order Number Input */}
        <div className="mb-6">
          <Label className="text-gray-700 font-bold text-sm uppercase tracking-wide mb-2 block">
            Número de Orden
          </Label>
          <div className="flex gap-2">
            <Input
              data-testid="order-input"
              type="number"
              placeholder="Ingresa el número de orden..."
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              onKeyPress={handleKeyPress}
              className="h-14 text-lg bg-white border-gray-300 focus:border-blue-600 focus:ring-blue-600 rounded-xl shadow-sm flex-1"
            />
            <Button
              data-testid="search-button"
              onClick={handleSearchOrder}
              disabled={isLoading}
              className="h-14 w-14 bg-red-600 hover:bg-red-700 text-white rounded-xl shadow-lg active:scale-95 transition-transform"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Search className="w-6 h-6" />
              )}
            </Button>
          </div>
        </div>

        {/* Order Details Card */}
        {orderDetails && (
          <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-500">
            {/* Order Info Card */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-md">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2
                    className="text-2xl font-bold text-gray-900 mb-1"
                    style={{ fontFamily: "Fraunces, serif" }}
                  >
                    Orden #{orderDetails.order_number || orderDetails.id}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {orderDetails.prep_time || orderDetails.created_at}
                  </p>
                </div>
                <div className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full">
                  <Check className="w-4 h-4" />
                  <span className="text-sm font-semibold">{orderDetails.status}</span>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4 mb-4">
                <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3">
                  Artículos del Pedido
                </h3>
                <ul className="space-y-2">
                  {orderDetails.items && orderDetails.items.length > 0 ? (
                    orderDetails.items.map((item, index) => (
                      <li key={index} className="flex items-center gap-2 text-gray-700">
                        <div className="w-1.5 h-1.5 bg-red-600 rounded-full"></div>
                        <span className="text-base">{item.name || item}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-gray-500 text-sm">No hay artículos disponibles</li>
                  )}
                </ul>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900">Total</span>
                  <span
                    className="text-2xl font-black text-red-600"
                    style={{ fontFamily: "Fraunces, serif" }}
                  >
                    ${orderDetails.total}
                  </span>
                </div>
              </div>
            </div>

            {/* Customer Info Card */}
            <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
              <h3
                className="text-xl font-bold text-gray-900 mb-4"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                Detalles del Cliente
              </h3>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Pizza className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-600 uppercase tracking-wide font-bold mb-1">
                      Nombre
                    </p>
                    <p className="text-base font-semibold text-gray-900">
                      {orderDetails.customer?.name || orderDetails.customer_name}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-600 uppercase tracking-wide font-bold mb-1">
                      Teléfono
                    </p>
                    <a
                      href={`tel:${orderDetails.customer?.phone || orderDetails.customer_phone}`}
                      className="text-base font-semibold text-green-600 hover:text-green-700"
                    >
                      {orderDetails.customer?.phone || orderDetails.customer_phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MessageCircleCode className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-600 uppercase tracking-wide font-bold mb-1">
                      Comentarios
                    </p>
                    <p className="text-base font-semibold text-gray-900 leading-relaxed">
                      {orderDetails.customer?.comments || orderDetails.customer_comments}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!orderDetails && !isLoading && (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-10 h-10 text-gray-400" />
            </div>
            <p className="text-gray-500 text-lg">Selecciona una sucursal e ingresa el número de orden</p>
          </div>
        )}

        {/* Error State */}
        {error && !orderDetails && !isLoading && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-800 font-semibold">Error</p>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          </div>
        )}
      </div>

      {/* Sticky Waze Navigation Button */}
      {orderDetails && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-white via-white to-transparent">
          <div className="max-w-md mx-auto">
            <Button
              data-testid="waze-btn"
              onClick={handleOpenWaze}
              disabled={!isNavigable} // PROPIEDAD CLAVE
              className={`
                rounded-full h-14 text-lg font-bold shadow-lg w-full flex items-center justify-center gap-2 transition-all
                ${isNavigable
                  ? "bg-blue-600 hover:bg-blue-700 text-white active:scale-95 shadow-blue-200"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed shadow-none"
                }
              `}
            >
              <Navigation className="w-6 h-6" />
              {isGeneric
                  ? "Coordenadas genéricas"
                  : hasCoordinates
                      ? "Navegar con Waze"
                      : "Coordenadas no disponibles"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
